import React, { useState, useEffect } from 'react';
import { RiUserLine, RiArrowDownSLine, RiCheckboxCircleLine, RiLockLine } from '@remixicon/react';
import { cn } from '../../../../components/ui/utils';
import ClientForm from './ClientForm';
import ClientSelector from './ClientSelector';
import { createClient } from '../../services/clientService';
import { useAuthStore } from '@store/authStore';
/**
 * Client Information Section Component
 * Card-style section for client information with New/Existing toggle
 *
 * @param {Object} props
 * @param {Object} props.data - Section data {type: 'new'|'existing', clientData: Object}
 * @param {Function} props.onUpdate - Callback when section updates
 * @param {boolean} props.isComplete - Whether section is complete
 * @param {boolean} props.isExpanded - Whether section is expanded
 * @param {Function} props.onToggle - Callback when section is toggled
 * @param {Function} props.onNext - Callback when Next button is clicked
 * @param {boolean} props.disabled - Whether section is disabled (locks everything incl. toggle)
 * @param {boolean} props.locked - Whether client edits are locked (read-only) but the
 *                                 section toggle still works. Used in edit mode for quotes
 *                                 already sent / viewed / signed.
 * @param {string} props.lockedReason - Optional reason text shown next to the lock icon.
 */
const ClientInformationSection = ({
  data = { type: 'new', clientData: {} },
  onUpdate,
  isComplete = false,
  isExpanded = false,
  onToggle,
  onNext,
  disabled = false,
  locked = false,
  lockedReason = '',
}) => {
  // Edit controls (mode toggle, form inputs, Next button) are locked when EITHER
  // `disabled` (full lockout) OR `locked` (read-only but navigable) is true.
  const editsDisabled = disabled || locked;

  const { userProfile } = useAuthStore();

  const [sectionData, setSectionData] = useState({
    type: 'new',
    clientData: {},
    ...data
  });
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  // Update state when data prop changes
  useEffect(() => {
    setSectionData(prev => ({
      ...prev,
      ...data
    }));
  }, [data]);

  // Handle mode toggle
  const handleModeChange = (newType) => {
    if (editsDisabled) return;

    // Don't update if already active
    if (newType === sectionData.type) return;

    const updatedData = {
      type: newType,
      clientData: newType === 'new' ? {} : null
    };
    setSectionData(updatedData);
    onUpdate?.(updatedData);
  };

  // Handle new client form change
  const handleNewClientChange = (formData) => {
    const updatedData = {
      type: 'new',
      clientData: formData
    };
    setSectionData(updatedData);
    onUpdate?.(updatedData);
  };

  // Handle existing client selection
  const handleExistingClientChange = (client) => {
    const updatedData = {
      type: 'existing',
      clientData: client
    };
    setSectionData(updatedData);
    onUpdate?.(updatedData);
  };

  // Handle Next button click. Next remains available when `locked` is true
  // (the section is read-only, but the broker still needs to navigate
  // forward) — only `disabled` (full lockout) blocks it.
  const handleNext = async () => {
    if (disabled || !isComplete) return;

    setError(null);

    // When locked, never re-create the client — just move forward.
    if (locked) {
      onNext?.();
      return;
    }

    // If client type is 'new', create the client via API
    if (sectionData.type === 'new') {
      try {
        setIsCreating(true);

        sectionData.clientData.broker_id = userProfile?.userprofile_id;
        
        // Call API to create new client
        const createdClient = await createClient(sectionData.clientData);

        // Update the section data with the created client (including client_id)
        // Ensure we have the snake_case fields that ClientSelector expects
        const updatedData = {
          type: 'existing', // Change to existing since we now have a client_id
          clientData: {
            ...createdClient,
            // Ensure snake_case fields are set from API response or original form data
            first_name: createdClient.first_name || sectionData.clientData.firstName,
            last_name: createdClient.last_name || sectionData.clientData.lastName,
            email: createdClient.email || sectionData.clientData.email,
            company: createdClient.company || sectionData.clientData.company,
            phone: createdClient.phone || sectionData.clientData.phone,
          }
        };
        setSectionData(updatedData);
        onUpdate?.(updatedData);
        
        // Call the onNext callback
        onNext?.();
      } catch (err) {
        console.error('Error creating client:', err);
        setError(err.message || 'Failed to create client. Please try again.');
      } finally {
        setIsCreating(false);
      }
    } else {
      // For existing client, just proceed to next section
      onNext?.();
    }
  };

  // Generate summary text for collapsed state
  const getSummaryText = () => {
    if (!sectionData.clientData) {
      return 'No client selected';
    }

    const client = sectionData.clientData;
    if (sectionData.type === 'existing') {
      const name = [client.first_name, client.last_name].filter(Boolean).join(' ');
      return name || client.email || 'Client selected';
    } else {
      const name = [client.firstName, client.lastName].filter(Boolean).join(' ');
      return name || client.email || 'New client';
    }
  };

  return (
    <div className="bg-white !rounded-2xl shadow-sm border border-gray-200">
      {/* Section Header - Always Visible */}
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className={cn(
          'w-full flex items-center justify-between p-6 text-left transition-colors',
          disabled && 'cursor-not-allowed opacity-60'
        )}
      >
        <div className="flex items-center gap-3 flex-1">
          <RiUserLine className="w-5 h-5 text-gray-700 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              Client Information
              {locked && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-600 border border-gray-200"
                  title={lockedReason || 'Client cannot be changed once the quote has been sent'}
                >
                  <RiLockLine className="w-3 h-3" />
                  Locked
                </span>
              )}
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isComplete && (
            <div className="flex items-center justify-center w-6 h-6 rounded-full">
              <RiCheckboxCircleLine className="w-6 h-6 text-primary-500" />
            </div>
          )}
        </div>
      </button>

      {/* Section Content - Shown when expanded */}
      {isExpanded && (
        <div className="px-6 pb-6">
          {/* Toggle Buttons */}
          <div className="flex gap-0 mb-6 bg-gray-100 rounded-lg p-1">
        <button
          type="button"
          onClick={() => handleModeChange('new')}
          disabled={editsDisabled}
          className={cn(
            'flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all',
            'focus:outline-none',
            sectionData.type === 'new'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900',
            editsDisabled && 'opacity-60 cursor-not-allowed'
          )}
        >
          New
        </button>
        <button
          type="button"
          onClick={() => handleModeChange('existing')}
          disabled={editsDisabled}
          className={cn(
            'flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all',
            'focus:outline-none',
            sectionData.type === 'existing'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900',
            editsDisabled && 'opacity-60 cursor-not-allowed'
          )}
        >
          Existing
        </button>
      </div>

      {/* Form Content */}
      <div className="space-y-4">
        {sectionData.type === 'new' ? (
          <ClientForm
            data={sectionData.clientData || {}}
            onChange={handleNewClientChange}
            errors={{}}
            disabled={editsDisabled}
          />
        ) : (
          <ClientSelector
            value={sectionData.clientData}
            onChange={handleExistingClientChange}
            error=""
            disabled={editsDisabled}
          />
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Next Button */}
      <div className="mt-6">
        <button
          type="button"
          onClick={handleNext}
          disabled={disabled || !isComplete || isCreating}
          className={cn(
            'w-full px-4 py-3 rounded-full text-sm font-medium transition-all',
            'bg-accent text-primary-500 hover:bg-accent',
            'focus:outline-none focus:ring-0 focus:ring-blue-0 focus:ring-offset-0',
            'flex items-center justify-center gap-2',
            (disabled || !isComplete || isCreating) && 'opacity-50 cursor-not-allowed'
          )}
        >
          {isCreating ? 'Creating Client...' : 'Next'}
          <RiArrowDownSLine className="w-4 h-4" />
        </button>
      </div>

        </div>
      )}
    </div>
  );
};

export default ClientInformationSection;
